import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {socket} from '../util/socket';
import {roomRunFileSelector, roomSelector} from '../store/selectors/roomRunFile.selectors';
import {questionUserAnswersCountSelector} from '../store/selectors/analytics.selectors';
import {fetchQuestionAnswersCountAction, updateQuestionReportAction} from '../store/actions/analytics.actions';
import {QuestionReport} from '../incase-domain/analytics';

export function useUserQuestionResponseCount(questionId: string, isReportsModalOpen = false): number {
    const dispatch = useDispatch();

    const questionAnswers = useSelector(questionUserAnswersCountSelector);
    const roomRunFile = useSelector(roomRunFileSelector);
    const {roomId, isPresenter} = useSelector(roomSelector);

    const questionAnswer = questionAnswers.find((qr) => qr.elementId === questionId);
    const [count, setCount] = useState(questionAnswer?.count || 0);

    useEffect(() => {
        if (socket && socket.connected && roomId && questionId && roomRunFile._id) {
            socket.emit('requestQuestionCount', {
                questionId,
                roomRunfileId: roomRunFile._id,
            });
        }

        if (isPresenter && roomId && questionId) {
            dispatch(fetchQuestionAnswersCountAction(questionId, roomId, roomRunFile._id));
        }
    }, [dispatch, questionId, roomId, roomRunFile._id, isPresenter]);

    useEffect(() => {
        if (questionAnswer?.count !== undefined) {
            setCount(questionAnswer.count);
        }
    }, [questionAnswer?.count]);

    useEffect(() => {
        const handleSocketEvent = (count: number): void => {
            setCount(count);
        };

        const handleQuestionReportEvent = (questionReport: QuestionReport): void => {
            dispatch(updateQuestionReportAction(questionReport));
        };

        if (socket) {
            socket.on(`userQuestionResponse-${questionId}`, handleSocketEvent);
            socket.on(`questionReport-${questionId}`, handleQuestionReportEvent);
        }

        return () => {
            if (socket) {
                socket.off(`userQuestionResponse-${questionId}`, handleSocketEvent);
                socket.off(`questionReport-${questionId}`, handleQuestionReportEvent);
            }
        };
    }, [dispatch, questionId, roomRunFile._id]);

    useEffect(() => {
        if (!socket || !roomId || !roomRunFile._id) {
            return undefined;
        }

        if (isReportsModalOpen) {
            socket.emit('subscribeQuestionReport', {
                questionId,
                roomRunfileId: roomRunFile._id,
            });
        } else {
            socket.emit('unsubscribeQuestionReport', {
                questionId,
            });
        }

        return () => {
            if (socket && socket.connected) {
                socket.emit('unsubscribeQuestionReport', {
                    questionId,
                });
            }
        };
    }, [isReportsModalOpen, questionId, roomId, roomRunFile._id]);

    return count;
}
